import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../Authen/AuthContext";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const NavBar = ({ currency, setCurrency }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [language, setLanguage] = useState(localStorage.getItem("language") || "en");
  const [darkMode, setDarkMode] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsMenuOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ โหลดภาษาเดิมจาก localStorage
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    logout();
    localStorage.removeItem("token");
    navigate("/loginform");
    closeMenu();
  };

  const toggleCurrency = () => {
    const newCurrency = currency === "THB" ? "USD" : "THB";
    setCurrency(newCurrency);
    localStorage.setItem("currency", newCurrency);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.body.style.backgroundColor = darkMode ? "#fff" : "#122843ff";
    document.body.style.color = darkMode ? "#000" : "#fff";
  };

  // ✅ เมื่อเปลี่ยนภาษา
  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  const styles = {
    nav: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: "linear-gradient(135deg, #0f1c2e 0%, #1d3557 50%, #457b9d 100%)",
      color: "#fff",
      padding: "10px 20px",
      position: "sticky",
      top: 0,
      zIndex: 1000,
      boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
      flexWrap: "wrap",
      width: "100%",
      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
      backdropFilter: "blur(12px) saturate(180%)",
    },
    logoContainer: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    logoImg: {
      height: "32px",
      marginRight: "10px",
    },
    links: {
      display: isMobile ? (isMenuOpen ? "flex" : "none") : "flex",
      flexDirection: isMobile ? "column" : "row",
      position: isMobile ? "absolute" : "static",
      top: isMobile ? "57px" : "auto",
      left: 0,
      width: isMobile ? "100%" : "auto",
      background: isMobile
        ? "linear-gradient(135deg, #031c3eff, #3b789eff)"
        : "transparent",
      padding: isMobile ? "15px" : 0,
      gap: "20px",
      justifyContent: isMobile ? "center" : "flex-start",
      alignItems: "center",
      zIndex: 999,
      fontFamily: "'Roboto Condensed', 'Kanit', sans-serif",
    },
    link: {
      fontFamily: "'Roboto Condensed', 'Kanit', sans-serif",
      color: "#fff",
      textDecoration: "none",
      fontSize: "18px",
      padding: "8px 12px",
      textTransform: "capitalize",
      borderRadius: "6px",
      transition: "all 0.3s ease",
    },
    linkHover: {
      color: "#fff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    },
    toggle: {
      display: isMobile ? "block" : "none",
      fontSize: "28px",
      cursor: "pointer",
      background: "none",
      border: "none",
      color: "#fff",
    },
    actionButton: {
      background: "#fff",
      color: "#142438",
      border: "none",
      borderRadius: "5px",
      padding: "6px 12px",
      fontWeight: "bold",
      cursor: "pointer",
      fontSize: isMobile ? "14px" : "12px",
      marginLeft: "10px",
    },
    langSelect: {
      border: "none",
      borderRadius: "5px",
      padding: "6px 12px",
      background: "#fff",
      color: "#142438",
      fontWeight: "bold",
      cursor: "pointer",
      fontSize: isMobile ? "14px" : "12px",
      marginLeft: "10px",
    },
  };

  return (
    <nav style={styles.nav}>
      {/* โลโก้ */}
      <div style={styles.logoContainer}>
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            textDecoration: "none",
          }}
        >
          <img src="/img/logo3.png" alt="Logo" style={styles.logoImg} />
          <span style={{ fontSize: "16px", color: "#FFF" }}>
            Summer House Agent
          </span>
        </Link>
      </div>

      {/* เมนู */}
      <div style={styles.links}>
        <Link
          to="/"
          onClick={closeMenu}
          style={{
            ...styles.link,
            ...(hoveredLink === "home" ? styles.linkHover : {}),
          }}
          onMouseEnter={() => setHoveredLink("home")}
          onMouseLeave={() => setHoveredLink(null)}
        >
          {t("home")}
        </Link>

        <Link
          to="/search"
          onClick={closeMenu}
          style={{
            ...styles.link,
            ...(hoveredLink === "search" ? styles.linkHover : {}),
          }}
          onMouseEnter={() => setHoveredLink("search")}
          onMouseLeave={() => setHoveredLink(null)}
        >
          {t("Search")}
        </Link>

        <Link
          to="/buy"
          onClick={closeMenu}
          style={{
            ...styles.link,
            ...(hoveredLink === "buy" ? styles.linkHover : {}),
          }}
          onMouseEnter={() => setHoveredLink("buy")}
          onMouseLeave={() => setHoveredLink(null)}
        >
          {t("buy")}
        </Link>

        <Link
          to="/rent"
          onClick={closeMenu}
          style={{
            ...styles.link,
            ...(hoveredLink === "rent" ? styles.linkHover : {}),
          }}
          onMouseEnter={() => setHoveredLink("rent")}
          onMouseLeave={() => setHoveredLink(null)}
        >
          {t("rent")}
        </Link>

        <Link
          to="/reviewpage"
          onClick={closeMenu}
          style={{
            ...styles.link,
            ...(hoveredLink === "reviewpage" ? styles.linkHover : {}),
          }}
          onMouseEnter={() => setHoveredLink("reviewpage")}
          onMouseLeave={() => setHoveredLink(null)}
        >
          {t("reviewpage")}
        </Link>

        {user?.role?.toLowerCase() === "admin" && (
          <div
            style={{ position: "relative", display: "inline-block" }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            <div
              style={{
                cursor: "pointer",
                fontWeight: "bold",
                padding: "10px 15px",
                color: "#fff",
                background: "linear-gradient(135deg, #dd9000ff, #e8d755ff)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {t("Admin")} <span style={{ fontSize: "12px" }}>▼</span>
            </div>

            {open && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "linear-gradient(135deg, #1d3557, #457b9d)",
                  borderRadius: "8px",
                  overflow: "hidden",
                  minWidth: "180px",
                  zIndex: 999,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                }}
              >
                <Link
                  to="/dashboardadmin"
                  onClick={closeMenu}
                  style={{
                    display: "block",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: "#fff",
                  }}
                >
                  {t("dashboardadmin")}
                </Link>
                <Link
                  to="/create"
                  onClick={closeMenu}
                  style={{
                    display: "block",
                    padding: "10px 15px",
                    textDecoration: "none",
                    color: "#fff",
                  }}
                >
                  {t("create_property")}
                </Link>
              </div>
            )}
          </div>
        )}

        {!user ? (
          <Link
            to="/loginform"
            onClick={closeMenu}
            style={{
              ...styles.link,
              ...(hoveredLink === "login" ? styles.linkHover : {}),
            }}
            onMouseEnter={() => setHoveredLink("login")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            {t("login")}
          </Link>
        ) : (
          <>
            <span style={{ color: "#fff", fontSize: "16px" }}>👤 {user.username}</span>
            <button
              onClick={handleLogout}
              style={{
                ...styles.link,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t("logout")}
            </button>
          </>
        )}

        <button onClick={toggleCurrency} style={styles.actionButton}>
          {currency === "THB" ? "THB ฿" : "USD $"}
        </button>

        <button onClick={toggleDarkMode} style={styles.actionButton}>
          {darkMode ? "☀️ Light" : "🌙 Dark"}
        </button>

        {/* ✅ dropdown ภาษาหลายภาษา */}
        <select value={language} onChange={handleLanguageChange} style={styles.langSelect}>
          <option value="en">🇬🇧 English</option>
          <option value="th">🇹🇭 ไทย</option>
          <option value="zh">🇨🇳 中文</option>
          <option value="ru">🇷🇺 Русский</option>
        </select>
      </div>

      {/* burger menu */}
      <button onClick={toggleMenu} style={styles.toggle}>
        {isMenuOpen ? "✕" : "☰"}
      </button>
    </nav>
  );
};

export default NavBar;
